import { Button, Dropdown, Input, Select, Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import { ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import CopyIcon from "../../../assets/icons/CopyIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import {
  useGetAllJobPostQuery,
  useUpdateStatusJobMutation,
} from "../../../features/employee/employeeApi";
import useCopy from "../../../hooks/useCopy";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import { GetAllJobPostParamsType } from "../../../types";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import { addKeyInArray, selectSearchOption } from "../../../utils/utils";

enum Status {
  active = "status-success",
  closed = "status-danger",
  inactive = "status-danger",
}

export enum EType {
  "full_time" = "Full Time",
  "part_time" = "Part Time",
  "contract" = "Contract",
}

export enum JType {
  "hybrid" = "Hybrid",
  "remote" = "Remote",
  "onsite" = "Onsite",
}

export default function JobPost() {
  const { authInfo } = useAppSelector((state) => state.auth);
  const [copyLoading, handleCopy] = useCopy();
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    keyword,
    handleFilter,
    handleSearch,
  } = useTable<GetAllJobPostParamsType>({
    defaultFilter: {
      business_id: authInfo?.business_id!,
    },
  });
  // const {
  //   isError: isDepartmentError,
  //   error: departmentError,
  //   data: departmentData,
  // } = useGetDepartmentsQuery({
  //   params: { skip: 0, limit: 0, is_published: 1 },
  // });
  const { isLoading, isFetching, isError, error, data } = useGetAllJobPostQuery(
    {
      params: { search: keyword, ...filter },
    }
  );
  // const [
  //   jobStatus,
  //   { isLoading: isStatusLoding, isError: isStatusError, error: statusError },
  // ] = useUpdateJobStatusMutation();

  // useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isError, error, "Get job post data fail");
  // useError(isStatusError, statusError, "Get job post data fail");

  const columns: ColumnsType<any> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: () => (
        <span className="inline-block min-w-[150px]">Job Title</span>
      ),
      dataIndex: "job_title",
      render: (value) => value || "n/a",
    },
    {
      title: () => <span className="inline-block min-w-[130px]">Job Type</span>,
      dataIndex: "employment_type",
      render: (val: keyof typeof JType) => <span>{JType[val]}</span>,
    },
    {
      title: () => (
        <span className="inline-block min-w-[130px]">Employment Type</span>
      ),
      dataIndex: "employment_status",
      render: (val: keyof typeof EType) => <span>{EType[val]}</span>,
    },
    {
      title: () => <span className="inline-block min-w-[100px]">Salary</span>,
      dataIndex: "job_salary",
      render: (value) => value || "Negotiable",
    },
    {
      title: "Vacancy",
      dataIndex: "no_of_vacancies",
      render: (value) => value || 0,
    },
    {
      title: () => (
        <span className="inline-block min-w-[130px]">Applicant Number</span>
      ),
      dataIndex: "no_of_applicant",
      render: (value) => value || 0,
    },
    {
      title: "Location",
      dataIndex: "job_location",
      render: (value) => value || "n/a",
    },
    {
      title: () => (
        <span className="inline-block min-w-[130px]">Job's Link</span>
      ),
      dataIndex: "id",
      render: (value) => {
        const url = `${window.location.origin}/job-post/${value}`;

        return (
          <div className="flex items-center gap-2 text-sm text-display-dark">
            <span className="truncate max-w-xs">{url.replace(/\\/g, "/")}</span>
            <button
              onClick={() => handleCopy(url)}
              className="text-[#1B4DFF] hover:text-[#1B4DFF]/70 p-1 rounded"
              title="Copy"
              disabled={copyLoading}
            >
              <CopyIcon />
            </button>
          </div>
        );
      },
    },
    {
      title: () => <span className="inline-block min-w-[100px]">Deadline</span>,
      dataIndex: "deadline",
      render: (value) => value || "n/a",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val: keyof typeof Status) => (
        <span className={Status[val]}>{capitalize(val)}</span>
      ),
    },
    {
      title: () => (
        <span className="inline-block min-w-[100px]">Published By</span>
      ),
      dataIndex: "createdByMember",
      render: (value) => value?.member?.profile?.name || "n/a",
    },
    {
      title: "Action",
      dataIndex: "id",
      align: "center",
      render: (val, record) => {
        return <ViewDropdown status={record.status} id={val} />;
      },
    },
  ];

  return (
    <SidebarLayout title="Job Post List">
      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalLength}
        dataSource={addKeyInArray(data?.data?.jobPosts)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search by job title"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={handleSearch}
            />
            <Select
              allowClear
              showSearch
              placeholder="Job Type"
              options={Object.entries(JType).map(([value, label]) => ({
                label,
                value,
              }))}
              onChange={(val) => handleFilter("employment_type", val)}
              filterOption={selectSearchOption}
              className="min-w-[100px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Select
              allowClear
              showSearch
              placeholder="Employment Type"
              options={Object.entries(EType).map(([value, label]) => ({
                label,
                value,
              }))}
              onChange={(val) => handleFilter("employment_status", val)}
              filterOption={selectSearchOption}
              className="min-w-[100px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Select
              allowClear
              showSearch
              placeholder="Status"
              options={Object.entries(Status).map(([value]) => ({
                label: capitalize(value),
                value,
              }))}
              onChange={(val) => handleFilter("status", val)}
              filterOption={selectSearchOption}
              className="min-w-[100px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Link to="/admin/recruitment/job-post/add">
              <Button
                icon={<AddIcon />}
                type="primary"
                className="sm-button !px-3"
              >
                Add new job
              </Button>
            </Link>
            {/* <Button
              icon={<SettingIcon />}
              type="primary"
              className="sm-button"
            /> */}
          </>
        }
      />
    </SidebarLayout>
  );
}

const ViewDropdown = ({ id, status }: { id?: string; status?: string }) => {
  const [openDropdown, setOpenDropdown] = useState(false);
  const { open, handleClose, handleOpen } = useModal();
  const [updateStatus, { isLoading, isError, error, isSuccess }] =
    useUpdateStatusJobMutation();
  useError(isError, error, "Job status updated fail");

  const handleChange = () => {
    handleOpen({});
  };

  useEffect(() => {
    if (isSuccess) {
      toast(
        "success",
        `Job post ${status === "active" ? "active" : "inactive"} successfully`
      );
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <>
      <ConfirmModal
        okText={status === "active" ? "Inactive" : "Active"}
        confirmTitle={status === "active" ? "Inactive this." : "Active this."}
        questionText={
          status === "active"
            ? "Are sure to inactive this item?"
            : "Are sure to active this item?"
        }
        open={open?.open}
        handleOk={() =>
          updateStatus({
            id: id as string,
            body: { status: status === "active" ? "inactive" : "active" },
          })
        }
        handleClose={handleClose}
        okLoading={isLoading}
      />
      <Dropdown
        trigger={["hover"]}
        placement="bottomRight"
        arrow={{ pointAtCenter: true }}
        open={openDropdown}
        onOpenChange={setOpenDropdown}
        dropdownRender={() => (
          <div className="w-36 bg-white shadow rounded-md p-1">
            <Link to={`/admin/recruitment/job-post/view/${id}`}>
              <DropdownIcon text="View" icon={<ViewIcon strokeWidth="1.3" />} />
            </Link>
            <Link to={`/admin/recruitment/job-post/update/${id}`}>
              <DropdownIcon text="Edit" icon={<EditIcon strokeWidth="1.1" />} />
            </Link>
            <div
              className={`flex items-center gap-2.5 text-sm font-medium text-display-body rounded hover:bg-gray/20 cursor-pointer px-3 py-1.5`}
              onClick={handleChange}
            >
              <Switch
                onChange={handleChange}
                checked={status?.toLowerCase() === "active"}
                size="small"
              />
              <span>
                {status?.toLowerCase() !== "active" ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        )}
      >
        <Button
          className="action-button"
          size="small"
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              className="lucide lucide-ellipsis-vertical-icon lucide-ellipsis-vertical size-4"
            >
              <circle cx="12" cy="12" r="1" />
              <circle cx="12" cy="5" r="1" />
              <circle cx="12" cy="19" r="1" />
            </svg>
          }
        />
      </Dropdown>
    </>
  );
};

type TDropdownIcon = {
  icon: ReactNode;
  text: string;
  onClick?: () => void;
};
const DropdownIcon = ({ icon, text, onClick }: TDropdownIcon) => (
  <div
    className="flex items-center gap-2.5 text-sm font-medium text-display-body rounded hover:bg-gray/20 cursor-pointer px-3 py-1.5"
    onClick={onClick}
  >
    <span className="w-4 flex items-center text-[#5E718D]">{icon}</span>
    <span>{text}</span>
  </div>
);
